import { useMutation } from "@tanstack/react-query";
import type { LinkedInImportStartedDto, LinkedInImportStatusDto } from "@reactive-resume/dto";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

/**
 * Import LinkedIn — scraping de l'URL publique du profil (Bright Data côté serveur).
 *  1. `startLinkedInImport`   → crée un job (validation URL + consentement + quota côté serveur) ;
 *  2. `pollLinkedInImport`    → progression ; quand `ready` : `draft` = profil normalisé ;
 *  3. `confirmLinkedInImport` → crée réellement le CV après prévisualisation utilisateur.
 */
export const startLinkedInImport = async (url: string, consent: boolean) => {
  const response = await axios.post<LinkedInImportStartedDto>("/linkedin/import/url", {
    url,
    consent,
  });
  return response.data;
};

export const pollLinkedInImport = async (jobId: string) => {
  const response = await axios.post<LinkedInImportStatusDto>("/linkedin/import/poll", { jobId });
  return response.data;
};

export const confirmLinkedInImport = async (jobId: string) => {
  const response = await axios.post<{ resumeId: string; duplicate: boolean }>(
    "/linkedin/import/confirm",
    {
      jobId,
    },
  );
  return response.data;
};

export const useStartLinkedInImport = () => {
  return useMutation({
    mutationFn: ({ url, consent }: { url: string; consent: boolean }) =>
      startLinkedInImport(url, consent),
  });
};

export const useConfirmLinkedInImport = () => {
  return useMutation({
    mutationFn: (jobId: string) => confirmLinkedInImport(jobId),
    onSuccess: () => {
      // Le CV est créé côté serveur → rafraîchit la liste des CVs.
      void queryClient.invalidateQueries({ queryKey: ["resumes"] });
    },
  });
};
